<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import TcModal from "@/Components/UI/TcModal.vue";
import TcTextarea from "@/Components/UI/TcTextarea.vue";
import TcButton from "@/Components/UI/TcButton.vue";

import useVideoCallStore from "../Store";

import { isValidString } from "@/Utils/String";

const MIN_RATING = 0;
const MAX_RATING = 5;
const OPTIONAL_NOTES_RATING = 4;

const { t } = useI18n();

const videoCallStore = useVideoCallStore();

const experience = ref<string>("");
const rating = ref<number>(MIN_RATING);
const isSending = ref<boolean>(false);

const emit = defineEmits<{
  (event: "onComplete"): void;
  (event: "onClose"): void;
}>();

const isNotesRequired = computed((): boolean => {
  return rating.value < OPTIONAL_NOTES_RATING;
});

const isComplete = computed((): boolean => {
  if (!isNotesRequired.value) return true;

  return rating.value > MIN_RATING && isValidString(experience.value);
});

const handleSendRating = async () => {
  if (isSending.value || !isComplete.value) return;

  isSending.value = true;

  await videoCallStore.sendVideoCallRating({ rate: rating.value, notes: experience.value });

  isSending.value = false;

  emit("onComplete");
};

const handleUpdateRate = (index: number) => {
  if (index === rating.value) {
    rating.value = MIN_RATING;

    return;
  }

  rating.value = index;
};
</script>

<template>
  <TcModal
    class="modal_rating"
    canClose
    :title="t('chat.modal.video_call.rating.title')"
    @clickOutside="emit('onClose')"
  >
    <template #description>
      <p class="text-center typography-low-l color-content-high mb-7">
        {{ t("chat.modal.video_call.rating.experience_title") }}
      </p>

      <div class="modal_rating__starts mb-6">
        <template v-for="index in MAX_RATING" :key="index">
          <img
            v-if="index <= rating"
            src="./assets/full_star.svg"
            alt="star"
            @click="() => handleUpdateRate(index)"
          />
          <img v-else src="./assets/empty_star.svg" alt="star" @click="() => handleUpdateRate(index)" />
        </template>
      </div>

      <TcTextarea
        v-model="experience"
        class="mb-6"
        isResizable
        :placeholder="`${t('chat.modal.video_call.rating.experience_placeholder')}${isNotesRequired ? '*' : ''}`"
        :rows="1"
      />

      <TcButton
        class="modal_rating__send"
        :disabled="!isComplete"
        :isLoading="isSending"
        @click="handleSendRating"
        isFitContent
      >
        {{ t("chat.modal.video_call.rating.rate") }}
      </TcButton>
    </template>
  </TcModal>
</template>

<style scoped lang="scss">
.modal_rating {
  &__starts {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: $space-5;

    img {
      height: $space-10;
      cursor: pointer;
    }
  }

  &__send {
    margin-left: auto;
    margin-right: auto;
    display: block;
  }
}
</style>

<style lang="scss">
.modal_rating {
  .tc-modal-content {
    width: 100%;

    .title > p {
      @include text.general(high, m);

      text-align: center;
    }
  }
}
</style>
